import CorrectIcon from "#/assets/exam-results/correct.svg?url";
import CorrectNotCheckedIcon from "#/assets/exam-results/CorrectNotChecked.svg?url";
import Line from "#/assets/exam-results/Line.svg";
import UncheckedIcon from "#/assets/exam-results/unchecked.svg?url";
import WrongIcon from "#/assets/exam-results/wrong.svg?url";
import EmptyState from "#/components/exam-results/EmptyState";
import QStatus from "#/components/exam-results/QStatus";
import Loading from "#/components/loading";
import { DqModal } from "#/components/Question/DqModal";
import { getFindSupplementaryExamResult } from "#/services/rest/gwt/exam-results/exam-results.api";
import type { FindSupplementaryExamResult } from "#/services/rest/gwt/exam-results/exam-results.types";
import { auth } from "#/utils";
import { useBreakLG, useBreakMD, useBreakXL } from "@monta/design-system";
import { useParams } from "@monta/router-react";
import { useMemo, useState } from "react";
import { useSelectedStudentIdContext } from "../context/studentsList/context";
import styles from "./styles.module.scss";

const guideItems = [
  { title: "درست زدی", icon: CorrectIcon },
  { title: "غلط زدی", icon: WrongIcon },
  { title: "گزینه درست سوالی که غلط زدی ", icon: CorrectNotCheckedIcon },
  { title: "گزینه درست سوالی که نزدی", icon: UncheckedIcon },
  { title: "سوال تشریحی", icon: Line }
];

const chunk = (arr, size) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
    arr.slice(i * size, i * size + size)
  );

const generateEmptyItem = (index: number): FindSupplementaryExamResult => {
  return {
    questionOrder: index + 1,
    isRemoved: true,
    questionID: index + 1,
    selectedChoiceOrder: 0,
    selectedChoiceID: 0,
    correctChoiceOrder: 0,
    correctChoiceID: 0,
    difficultyLevel: 0,
    mainTopic: "",
    subTopic: "",
    moduleGroupId: 0,
    moduleGroupName: ""
  };
};

const AnswerKey = () => {
  const params = useParams();
  const examTakingId = Number(params["examTakingId"]);
  const user = auth.useAuth();
  const breakXl = useBreakXL();
  const breakLg = useBreakLG();
  const breakMd = useBreakMD();
  const [questionModalId, setQuestionModalId] = useState<null | number>(null);
  const { selectedStudentId } = useSelectedStudentIdContext();

  const { data, isLoading } = getFindSupplementaryExamResult.useQuery({
    request: {
      body: {
        examTakingDetailId: examTakingId,
        studentId: selectedStudentId ?? user?.id
      }
    }
  });

  const dataChunks = useMemo(() => {
    if (!data) return;

    const emptyItems: FindSupplementaryExamResult[] = [];
    const sheetSize = data?.length < 150 ? 150 : 350;

    for (let index = data.length; index < sheetSize; index++)
      emptyItems.push(generateEmptyItem(index));

    return data ? chunk(data.concat(emptyItems), 10) : [];
  }, [data]);

  const generateColumns = useMemo(() => {
    if (!data || !dataChunks) return { columns: 5, rows: 1 };
    if (breakXl) return { columns: 3, rows: Math.ceil(dataChunks?.length / 5) };
    else if (breakLg)
      return { columns: 4, rows: Math.ceil(dataChunks?.length / 4) };
    else if (breakMd)
      return { columns: 3, rows: Math.ceil(dataChunks?.length / 3) };
    else return { columns: 2, rows: Math.ceil(dataChunks?.length / 2) };
  }, [data, dataChunks, breakXl, breakLg, breakMd]);

  return (
    <>
      <DqModal
        questionModalId={questionModalId}
        setQuestionModalId={setQuestionModalId}
      />
      <section className={styles["guide"]}>
        <div className={styles["guide__title"]}>راهنمای کلید:</div>
        <div className={styles["guide__items"]}>
          {guideItems.map((item, index) => {
            return (
              <div className={styles["guide__items__item"]} key={index}>
                <img src={item.icon} alt={item.title} />
                <div>{item.title}</div>
              </div>
            );
          })}
        </div>
      </section>

      {isLoading ? (
        <Loading />
      ) : dataChunks ? (
        <div
          className={styles["questions"]}
          style={{
            gridTemplate: `repeat(${generateColumns.rows}, 1fr) / repeat(${generateColumns.columns}, 1fr)`
          }}
        >
          {dataChunks.map((chunk: FindSupplementaryExamResult[], index) => {
            return (
              <div key={index + "chunk"} className={styles["questions__item"]}>
                {chunk.map((item) => {
                  return (
                    <div
                      key={item.questionID}
                      className={styles["questions__item__row"]}
                      onClick={() =>
                        !item.isRemoved && setQuestionModalId(item.questionID)
                      }
                    >
                      <span>{item.questionOrder}</span>
                      {item.correctChoiceOrder === null ? (
                        <div
                          className={styles["questions__item--descriptive"]}
                        ></div>
                      ) : (
                        <></>
                      )}
                      <QStatus
                        correctChoice={item.correctChoiceOrder}
                        selectedChoice={item.selectedChoiceOrder}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState />
      )}
    </>
  );
};

export default AnswerKey;
